import { useMemo, useRef } from 'react';
import { useGetPreAccountingCustomersQuery } from '../api/endpoints/preAccounting';

/**
 * Pre-Accounting Customer Dropdown için özelleştirilmiş hook
 * 
 * Bu hook, customer dropdown'ları için tutarlı bir data handling sağlar:
 * - Cache mekanizması ile UI flickering önlenir
 * - Farklı API response formatlarına karşı esnek yapı
 * - Type safety ve data validation
 * 
 * @param {Object} params - Query parametreleri
 * @param {string} params.SearchText - Arama metni (default: '')
 * @param {number} params.RowCount - Maksimum sonuç sayısı (default: 50)
 * @param {boolean} params.skip - Query'yi atla (default: false)
 * @returns {Object} { customerOptions, isFetching }
 * 
 * @example
 * const { customerOptions, isFetching } = usePreAccountingCustomerOptions({ SearchText: 'test' });
 */
export const usePreAccountingCustomerOptions = (params = {}) => {
  const prevOptionsRef = useRef([]);
  
  const { 
    SearchText = '', 
    RowCount = 50, 
    skip = false 
  } = params;

  const { data: customerData, isFetching } = useGetPreAccountingCustomersQuery(
    {
      SearchText,
      RowCount
    },
    { skip }
  );

  const customerOptions = useMemo(() => {
    // API'den veri gelmezse (loading/error) önceki cache'i dön
    if (!customerData) return prevOptionsRef.current;
    
    // Farklı API response formatlarını handle et
    const mapped = customerData.map(c => {
      // Label için farklı field'ları kontrol et (fallback chain)
      const label = c.name || c.fullName || c.customerName || c.dealerName || c.label || 'İsimsiz';
      
      // Value için farklı field'ları kontrol et (fallback chain)
      const value = c.id || c.customerId || c.dealerId || c.value;
      
      return { 
        label: String(label), // Type safety: Her zaman string
        value: value != null ? Number(value) : null // Type safety: Her zaman number veya null
      };
    }).filter(opt => opt.value != null); // Geçersiz kayıtları filtrele
    
    // Sadece geçerli veri varsa cache'i güncelle (boş array ile üzerine yazma)
    if (mapped.length > 0) {
      prevOptionsRef.current = mapped;
    }
    
    return mapped;
  }, [customerData]);

  return { 
    customerOptions, 
    isFetching 
  };
};
